import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../../Layout/DashboardLayout";
import { Dropdown, employeeID, companyID, sanitizeLabName, sanitizeEmailDomain, normalizeSpaces } from "../../../Common/UIComponents";
import {
  fetchMailMethods,
  fetchMailConfigDetails,
  saveEmailConfig,
} from "../../../API_Service/Settings_Slice";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const isValidEmail = (val) =>
  /^(?!.*\.\.)[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)*\.[a-zA-Z]{2,}$/.test(String(val).trim());

const isValidHostname = (val) =>
  /^(?!.*\.\.)[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*$/.test(String(val).trim());

const FIELD_MAX_LENGTH = 400;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#3e494a]/60"}`}>
      <span>{len}/{max}</span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

const METHOD_ICONS = {
  GRAPHAPI: "cloud",
  SMTP:     "dns",
};

const METHOD_DESCRIPTIONS = {
  GRAPHAPI: "Use Microsoft Azure AD and the Graph API to send mail via Microsoft 365. Ideal for enterprise environments with OAuth2 authentication.",
  SMTP:     "Send mail using a standard SMTP server. Compatible with Gmail, Outlook, and most mail providers.",
};

// ─────────────────────────────────────────────
// FIELD LABEL
// ─────────────────────────────────────────────

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-bold text-[#3e494a] uppercase tracking-widest ml-1 mb-2">
    {children}
    {required && <span className="text-[#ba1a1a] ml-1">*</span>}
  </label>
);

// ─────────────────────────────────────────────
// TEXT INPUT
// ─────────────────────────────────────────────

const TextInput = ({ type = "text", value, onChange, placeholder, error, disabled, maxLength, numericOnly = false }) => {
  const [showPwd, setShowPwd] = useState(false);
  const isPassword = type === "password";
  const inputType  = isPassword ? (showPwd ? "text" : "password") : type;

  const handleChange = (e) => {
    if (numericOnly) {
      const digits = e.target.value.replace(/\D/g, "");
      onChange({ target: { value: digits } });
    } else {
      const val = e.target.value.replace(/^\s+/, "");
      onChange({ ...e, target: { ...e.target, value: val } });
    }
  };

  return (
    <div>
      <div className={`flex items-center bg-[#e5e9e9] rounded-xl px-4 py-3 gap-2
        focus-within:ring-2 focus-within:ring-[#08757c] focus-within:bg-white transition-all
        ${error    ? "ring-2 ring-[#ba1a1a]" : ""}
        ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
        <input
          type={inputType}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          inputMode={numericOnly ? "numeric" : undefined}
          className="flex-1 bg-transparent outline-none text-sm text-[#181c1d] placeholder:text-[#3e494a]/40"
        />
        {isPassword && (
          <button type="button" onClick={() => setShowPwd((p) => !p)}
            className="text-[#3e494a]/60 hover:text-[#08757c] transition-colors">
            <span className="material-symbols-outlined text-[18px]">
              {showPwd ? "visibility_off" : "visibility"}
            </span>
          </button>
        )}
      </div>
      {error && <p className="text-xs text-[#ba1a1a] mt-1 ml-1">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────
// SSL TOGGLE
// ─────────────────────────────────────────────

const SSLToggle = ({ value, onChange }) => {
  const isOn = value === "1" || value === true || value === "true";
  return (
    <button type="button" onClick={() => onChange(isOn ? "0" : "1")}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${isOn ? "bg-[#08757c]" : "bg-[#bdc9c9]"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
        ${isOn ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function EmailConfigIndex({ embedded = false }) {
  const dispatch = useDispatch();

  const [mailMethodOptions, setMailMethodOptions] = useState([]);
  const [activeMethodID,    setActiveMethodID]    = useState(null);
  const [activeMethodValue, setActiveMethodValue] = useState("");
  const [dynamicFields,     setDynamicFields]     = useState([]);
  const [form,              setForm]              = useState({});
  const [errors,            setErrors]            = useState({});
  const [isLoading,         setIsLoading]         = useState(true);
  const [isFieldsLoading,   setIsFieldsLoading]   = useState(false);
  const [isSaving,          setIsSaving]          = useState(false);
  const [saveSuccess,       setSaveSuccess]       = useState(false);
  const [saveError,         setSaveError]         = useState(null);

  const loadFieldsForMethod = useCallback((methodID) => {
    if (!methodID) return;
    setIsFieldsLoading(true);
    setDynamicFields([]);
    setForm({});
    setErrors({});
    dispatch(fetchMailConfigDetails({ companyID, mailMethodID: methodID }))
      .then((res) => {
        const fields = toArray(res);
        setDynamicFields(fields);
        const prefilled = {};
        fields.forEach((f) => {
          const key = f.KeyCode ?? f.keyCode ?? "";
          prefilled[key] = f.KeyValue ?? f.keyValue ?? "";
        });
        setForm(prefilled);
      })
      .catch(() => {})
      .finally(() => setIsFieldsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchMailMethods({ companyID }))
      .then((res) => {
        const methods = toArray(res);
        const options = methods.map((m) => ({
          label:      m.DisplayName  ?? m.displayName  ?? "",
          value:      m.MethodCode   ?? m.methodCode   ?? "",
          id:         m.MailMethodID ?? m.mailMethodID ?? 0,
          isSelected: !!(m.IsSelected ?? m.isSelected),
        }));
        setMailMethodOptions(options);
        const active = options.find((o) => o.isSelected) ?? options[0];
        if (active) {
          setActiveMethodValue(active.value);
          setActiveMethodID(active.id);
          loadFieldsForMethod(active.id);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMethodChange = useCallback((val) => {
    const selected = mailMethodOptions.find((m) => m.value === val);
    if (!selected) return;
    setActiveMethodValue(val);
    setActiveMethodID(selected.id);
    setSaveSuccess(false);
    setSaveError(null);
    loadFieldsForMethod(selected.id);
  }, [mailMethodOptions, loadFieldsForMethod]);

  const handleField = useCallback((key) => (val) => {
    const raw = val?.target ? val.target.value : val;
    const k = key.toLowerCase();

    // Don't sanitize secret/password fields
    const isSecret = k.includes("secret") || k.includes("password") || k.includes("key");
    if (isSecret) {
      setForm((prev) => ({ ...prev, [key]: raw }));
      setErrors((prev) => ({ ...prev, [key]: "" }));
      setSaveSuccess(false);
      setSaveError(null);
      return;
    }

    // Sanitize based on field type
    let value = raw;
    if (typeof raw === "string") {
      const isServer = k.includes("host") || k.includes("server") || k.includes("smtp");
      const isClientId = key === "ClientId" || key === "clientId" || k === "clientid";
      const isTenantId = k.includes("tenant");
      const isSenderMail = k.includes("sender");
      const isDomain = key === "AllowedDomain" || key === "allowedDomain" || k === "alloweddomain";

      if (isServer || isClientId || isTenantId) {
        value = sanitizeLabName(raw);
      } else if (isSenderMail) {
        // For email addresses: allow alphanumeric, dots, hyphens, @, underscore, plus
        value = raw.replace(/[^a-zA-Z0-9.\-@_+]/g, "");
      } else if (isDomain) {
        value = sanitizeEmailDomain(raw);
      }
    }

    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setSaveSuccess(false);
    setSaveError(null);
  }, []);

  const validate = () => {
    const errs = {};
    if (!activeMethodValue) { errs.mailMethod = "Please select a mail method."; return errs; }
    dynamicFields.forEach((f) => {
      const key      = f.KeyCode        ?? f.keyCode        ?? "";
      const label    = f.KeyDisplayName ?? f.keyDisplayName ?? key;
      const required = f.IsRequired     ?? f.isRequired     ?? false;
      const isSSL      = key.toLowerCase().includes("ssl");
      const isEmail    = key.toLowerCase().includes("mail") || key.toLowerCase().includes("email");
      const isUsername = key.toLowerCase().includes("user");
      const fieldVal   = String(form[key] ?? "").trim();
      const looksLikeEmail = fieldVal.includes("@");

      // Required check
      if (required && !isSSL && !fieldVal)
        errs[key] = `${label} is required.`;

      // Email format check — only when a value is present
      if (isEmail && !isSSL && fieldVal && !isValidEmail(form[key]))
        errs[key] = `${label} must be a valid email address.`;

      // Username: if value looks like an email, validate format
      if (isUsername && !isSSL && looksLikeEmail && !isValidEmail(form[key]))
        errs[key] = `${label} must be a valid email format.`;

      // Server/host: if value has @ validate as email, otherwise validate as hostname
      const isServer = !isEmail && !isUsername && (
        key.toLowerCase().includes("host") || key.toLowerCase().includes("server") || key.toLowerCase().includes("smtp")
      );
      if (isServer && !isSSL && fieldVal) {
        if (looksLikeEmail && !isValidEmail(form[key]))
          errs[key] = `${label} must be a valid email format.`;
        else if (!looksLikeEmail && !isValidHostname(form[key]))
          errs[key] = `${label} must be a valid server name (e.g. smtp.gmail.com).`;
      }
    });
    return errs;
  };

  const handleSave = useCallback(async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const payload = {
      CompanyID:      companyID,
      CreatedBy:      employeeID,
      MailConfigList: dynamicFields.map((f) => {
        const key = f.KeyCode ?? f.keyCode ?? "";
        const fieldValue = String(form[key] ?? "");
        return {
          MailMethodID:           activeMethodID,
          MailConfigFieldID:      f.MailConfigFieldID      ?? f.mailConfigFieldID      ?? 0,
          MailConfigFieldValueID: f.MailConfigFieldValueID ?? f.mailConfigFieldValueID ?? 0,
          KeyCode:                key,
          KeyValue:               normalizeSpaces(fieldValue),
          IsRequired:             f.IsRequired ?? f.isRequired ?? false,
        };
      }),
    };
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    dispatch(saveEmailConfig(payload))
      .then((res) => {
        const result = res?.payload?.data ?? res?.payload;
        if (Number(result) > 0) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 4000);
          setForm((prev) =>
            Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, typeof v === "string" ? normalizeSpaces(v) : v]))
          );
        }
        else setSaveError("Failed to save. Please try again.");
      })
      .catch((err) => setSaveError(err?.message ?? "Failed to save configuration."))
      .finally(() => setIsSaving(false));
  }, [form, dynamicFields, activeMethodID, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedMethodLabel = mailMethodOptions.find((m) => m.value === activeMethodValue)?.label ?? "";
  const selectedMethodDesc  = METHOD_DESCRIPTIONS[activeMethodValue] ?? "";

  const SaveBtn = () => (
    <button onClick={handleSave} disabled={isSaving}
      className="px-8 py-2.5 bg-gradient-to-br from-[#08757c] to-[#005b60] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#08757c]/20 hover:opacity-90 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
      {isSaving
        ? <><span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>Saving…</>
        : <><span className="material-symbols-outlined text-[18px]">save</span>Save Configuration</>}
    </button>
  );

  const content = (
    <div className={embedded ? "text-[#181c1d]" : "px-8 py-8 bg-[#f7fafa] min-h-screen text-[#181c1d]"}>

      {/* Breadcrumb — only when standalone */}
      {!embedded && (
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>App Configuration</span>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#08757C] font-semibold">Email Configuration</span>
        </nav>
      )}

      {/* Heading + save — only when standalone */}
      {!embedded && (
        <div className="flex flex-wrap justify-between items-end mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#181c1d]">Email Configuration</h1>
            <p className="text-[#3e494a] text-sm mt-1">
              Configure the outgoing mail method used for system notifications and alerts.
            </p>
          </div>
          {activeMethodValue && !isFieldsLoading && <SaveBtn />}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-48 text-[#3e494a] text-sm gap-3">
          <span className="material-symbols-outlined animate-spin text-[#08757c]">progress_activity</span>
          Loading configuration…
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-8">

          {/* LEFT */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
              <h3 className="text-base font-bold flex items-center gap-2 mb-5">
                <span className="material-symbols-outlined text-[#08757c] text-[20px]">mail</span>
                Mail Send Method
              </h3>
              <FieldLabel required>Select Mail Send Method</FieldLabel>
              <Dropdown options={mailMethodOptions} value={activeMethodValue}
                onChange={handleMethodChange} placeholder="Select method…" error={errors.mailMethod} />
            </div>

            {activeMethodValue && (
              <div className="bg-white p-6 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#e8f4f5] flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-[#08757c] text-[20px]">
                      {METHOD_ICONS[activeMethodValue] ?? "settings"}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#181c1d]">{selectedMethodLabel}</p>
                    <p className="text-[10px] text-[#3e494a]/60 uppercase tracking-widest mt-0.5">Active Method</p>
                  </div>
                </div>
                <p className="text-xs text-[#3e494a] leading-relaxed">{selectedMethodDesc}</p>
              </div>
            )}

            <div className="bg-[#f1f4f4] p-4 rounded-xl flex gap-3 items-start border border-[#bdc9c9]/10">
              <span className="material-symbols-outlined text-[#08757c] text-[18px] mt-0.5">verified_user</span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#3e494a]">Validation</p>
                <p className="text-xs text-[#181c1d] mt-0.5">All fields marked with * are mandatory.</p>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="col-span-12 lg:col-span-8 space-y-6">
            {activeMethodValue && (
              <div className="bg-white p-8 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
                <h3 className="text-base font-bold flex items-center gap-2 mb-6">
                  <span className="material-symbols-outlined text-[#08757c] text-[20px]">
                    {METHOD_ICONS[activeMethodValue] ?? "settings"}
                  </span>
                  {selectedMethodLabel} Settings
                </h3>
                {isFieldsLoading ? (
                  <div className="flex items-center gap-3 text-sm text-[#3e494a] py-8">
                    <span className="material-symbols-outlined animate-spin text-[#08757c]">progress_activity</span>
                    Loading fields…
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                    {dynamicFields.map((f) => {
                      const key      = f.KeyCode        ?? f.keyCode        ?? "";
                      const label    = f.KeyDisplayName ?? f.keyDisplayName ?? key;
                      const required = f.IsRequired     ?? f.isRequired     ?? false;
                      const isPwd    = key.toLowerCase().includes("password") || key.toLowerCase().includes("secret");
                      const isSSL    = key.toLowerCase().includes("ssl") || key.toLowerCase().includes("enablessl");
                      const isPort   = key.toLowerCase().includes("port");
                      const maxLength = isPort ? 5 : FIELD_MAX_LENGTH;

                      if (isSSL) return (
                        <div key={key} className="col-span-2">
                          <div className="flex items-center justify-between bg-[#f7fafa] rounded-xl px-4 py-3.5">
                            <div>
                              <p className="text-sm font-semibold text-[#181c1d]">{label}</p>
                              <p className="text-xs text-[#3e494a]/60 mt-0.5">Recommended for secure connections (port 587 / 465)</p>
                            </div>
                            <SSLToggle value={form[key]}
                              onChange={(val) => { setForm((prev) => ({ ...prev, [key]: val })); setSaveSuccess(false); }} />
                          </div>
                        </div>
                      );

                      return (
                        <div key={key}>
                          <FieldLabel required={required}>{label}</FieldLabel>
                          <TextInput type={isPwd ? "password" : "text"} value={form[key] ?? ""}
                            onChange={handleField(key)} placeholder={`Enter ${label}`} error={errors[key]} maxLength={maxLength} numericOnly={isPort} />
                          <CharCounter value={form[key] ?? ""} max={maxLength} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {saveSuccess && (
              <div className="bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
                <span className="material-symbols-outlined text-[20px]">check_circle</span>
                <span className="font-semibold">Email configuration saved successfully.</span>
              </div>
            )}
            {saveError && (
              <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
                <span className="material-symbols-outlined text-[20px]">error</span>
                {saveError}
              </div>
            )}

            {activeMethodValue && !isFieldsLoading && (
              <div className="flex justify-end">
                <SaveBtn />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return <DashboardLayout>{content}</DashboardLayout>;
}
